import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { askAcademyGuide } from "@/lib/academy.functions";
import { AcademyNav } from "@/components/academy/AcademyNav";
import ReactMarkdown from "react-markdown";
import { Send, Sparkles } from "lucide-react";

export const Route = createFileRoute("/_authenticated/academy/ai-guide")({
  head: () => ({ meta: [{ title: "AI Guide · Academy" }, { name: "robots", content: "noindex" }] }),
  component: AIGuidePage,
});

type Msg = { role: "user" | "assistant"; content: string };

function AIGuidePage() {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");

  const ask = useMutation({
    mutationFn: (question: string) => askAcademyGuide({ data: { question, domainSlug: "human-design" } }),
    onSuccess: (res) => setMessages((m) => [...m, { role: "assistant", content: res.answer }]),
    onError: (e: Error) => setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${e.message}` }]),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const q = input.trim();
    if (!q || ask.isPending) return;
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    ask.mutate(q);
  }

  const suggestions = [
    "Explain the Sacral authority simply.",
    "Compare Generators and Manifesting Generators.",
    "What should I study next after Types?",
    "Give me a 3-minute meditation for deconditioning.",
  ];

  return (
    <>
      <AcademyNav />
      <div className="min-h-dvh bg-obsidian text-foreground">
        <section className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gold flex items-center gap-2"><Sparkles size={14}/> AI Guide</p>
          <h1 className="mt-2 font-serif text-3xl text-gold-luminous">Ask anything about the canon.</h1>

          <ul className="mt-6 space-y-4">
            {messages.length === 0 && (
              <li className="rounded-2xl border hairline p-4">
                <p className="text-sm text-foreground/70">Start with a question, or try one:</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {suggestions.map((s) => (
                    <button key={s} onClick={() => { setInput(s); }} className="rounded-full border hairline px-3 py-1.5 text-xs hover:border-gold/60">
                      {s}
                    </button>
                  ))}
                </div>
              </li>
            )}
            {messages.map((m, i) => (
              <li key={i} className={m.role === "user" ? "text-right" : ""}>
                <div className={`inline-block max-w-[85%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "bg-gold text-obsidian" : "border hairline bg-black/40"}`}>
                  {m.role === "assistant"
                    ? <div className="prose prose-invert prose-sm max-w-none"><ReactMarkdown>{m.content}</ReactMarkdown></div>
                    : m.content}
                </div>
              </li>
            ))}
            {ask.isPending && <li className="text-sm text-foreground/60">Thinking…</li>}
          </ul>

          <form onSubmit={submit} className="sticky bottom-4 mt-8 flex gap-2 rounded-full border hairline bg-obsidian/80 p-2 backdrop-blur">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Ask the Guide…"
              className="flex-1 bg-transparent px-3 py-2 text-sm outline-none" />
            <button type="submit" disabled={ask.isPending || !input.trim()}
              className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-4 py-2 text-sm font-semibold text-obsidian disabled:opacity-50">
              <Send size={14} /> Send
            </button>
          </form>
        </section>
      </div>
    </>
  );
}
